import { useState } from 'react';
import type React from 'react';

import { useGetFaceAttributeSamples } from '@vlabs/api/luna-api/client';
import type { FaceId, Similarity } from '@vlabs/api/luna-api/model';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import {
  AdditionalPropertiesBySampleId,
  TopMatchFaceAttributes,
  TopMatchFaceInfo,
} from '@vlabs/monokit/entities/event-details';
import { GoToFaceDetailsButton } from '@vlabs/monokit/features/go-to-face-details';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useTopMatchFaceTranslation } from './i18n';
import st from './TopMatchFace.module.sass';

type Tab = 'attributes' | 'additionalProperties';

interface TopMatchFaceProps extends WithDataTestid, WithClassNameProp {
  faceId: FaceId;
  similarity: Similarity;
}

const displayName = 'TopMatchFace';
const testid = buildTestid(displayName);

interface AttributesTabProps extends WithDataTestid {
  faceId: FaceId;
}

function AttributesTab({
  faceId,
  'data-testid': dataTestId = undefined,
}: AttributesTabProps): React.JSX.Element {
  return (
    <TopMatchFaceAttributes
      className={st.Attributes}
      faceId={faceId}
      {...testid(dataTestId)}
    />
  );
}

interface AdditionalPropertiesTabProps extends WithDataTestid {
  faceId: FaceId;
}

function AdditionalPropertiesTab({
  faceId,
  'data-testid': dataTestId = undefined,
}: AdditionalPropertiesTabProps): React.JSX.Element {
  const { t } = useTopMatchFaceTranslation();
  const { data: faceAttributeSamples } = useGetFaceAttributeSamples(faceId);
  const sampleId = faceAttributeSamples?.samples?.[0];

  if (!sampleId) return <span className={st.Empty}>{t('no_data')}</span>;

  return (
    <AdditionalPropertiesBySampleId
      className={st.AdditionalProperties}
      sampleId={sampleId}
      {...testid(dataTestId)}
    />
  );
}

function TopMatchFace({
  faceId,
  similarity,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: TopMatchFaceProps): React.JSX.Element {
  const { t } = useTopMatchFaceTranslation();
  const [tab, setTab] = useState<Tab>('attributes');

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      <h5>{t('title')}</h5>
      <div className={st.Panel}>
        <div className={st.Header}>
          <ButtonSwitchGroup
            onChange={setTab}
            size="small"
            value={tab}
            variant="square"
            {...testid(dataTestId)}
          >
            <ButtonSwitch value="attributes">
              {t('attributes')}
            </ButtonSwitch>
            <ButtonSwitch value="additionalProperties">
              {t('additional_properties')}
            </ButtonSwitch>
          </ButtonSwitchGroup>

          <GoToFaceDetailsButton
            faceId={faceId}
            variant="filled"
            {...testid(dataTestId)}
          />
        </div>

        <div className={st.Content}>
          <TopMatchFaceInfo
            faceId={faceId}
            similarity={similarity}
            {...testid(dataTestId)}
          />
          {tab === 'attributes' && (
            <AttributesTab
              faceId={faceId}
              {...testid(dataTestId)}
            />
          )}
          {tab === 'additionalProperties' && (
            <AdditionalPropertiesTab
              faceId={faceId}
              {...testid(dataTestId)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

TopMatchFace.displayName = displayName;

export { TopMatchFace };
